import { Box, Flex, Grid, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getFromUrl } from '../../api/Api';
import { PrimaryButton } from '../../components/Buttons/Buttons';
import CaseCard from '../../components/Cases/CaseCard';
import { Icon } from '../../components/Icons';
import { SearchInput } from '../../components/Inputs/TextField';
import PopoverMenu from '../../components/Popover';
import Tabs from '../../components/Tabs';
import { Body, Subtitle } from '../../components/Texts/Texts';
import DashboardLayout from '../../layouts/dashboard/DashboardLayout';
import { colors } from '../../theme/colors';
import { ICaseData } from '../../types';
import { hexToRgbA } from '../../utils/helper';
import {
  resolveCasesListState,
  saveCasesListItems,
  saveCasesListState
} from '../../utils/casesListState';
import EmptyCases from './EmptyCases';
import { getCurrentRole, UserRole } from '../../constants/roles';

const PAGE_SIZE_OPTIONS = [12, 24, 36, 48, 60];

const Cases = () => {
  const location: any = useLocation();
  const restoredListState = resolveCasesListState(location.state);
  const [data, setData] = useState<ICaseData[]>([]);
  const [activeTab, setActiveTab] = useState(restoredListState.tab);
  const [filter, setFilter] = useState(restoredListState.filter);
  const [searchKeyword, setSearchKeyword] = useState(restoredListState.search);
  const [debouncedSearch, setDebouncedSearch] = useState(restoredListState.search);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(restoredListState.page);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(restoredListState.pageSize);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentRole = getCurrentRole();
  const canViewReferredCases =
    currentRole === UserRole.ADMIN ||
    currentRole === UserRole.SUPER_ADMIN ||
    currentRole === UserRole.SUPERVISOR;
  const caseTabs = canViewReferredCases
    ? ['timeline', 'escalated', 'reported', 'assigned', 'referred', 'closed']
    : ['timeline', 'escalated', 'reported', 'assigned', 'closed'];

  const severityFilters = [
    'all',
    'mild',
    'moderate',
    'moderately severe',
    'severe'
  ];

  const listState = {
    page: currentPage,
    tab: activeTab,
    filter,
    pageSize,
    search: debouncedSearch
  };

  useEffect(() => {
    saveCasesListState(listState);
  }, [currentPage, activeTab, filter, pageSize, debouncedSearch]);

  useEffect(() => {
    if (data?.length) {
      saveCasesListItems(data);
    }
  }, [data]);

  const buildSearchQuery = (keyword = debouncedSearch) => {
    const trimmed = (keyword || '').trim();
    return trimmed ? `&search=${encodeURIComponent(trimmed)}` : '';
  };

  const buildEndpoint = (
    tab = activeTab,
    page = currentPage,
    size = pageSize,
    severityFilter = filter,
    search = debouncedSearch
  ) => {
    const searchQuery = buildSearchQuery(search);
    const pageQuery = `page=${page + 1}&pageSize=${size}`;

    if (severityFilter === 'all') {
      if (tab === 'timeline') {
        return `cases?severity=ALL&${pageQuery}${searchQuery}`;
      }
      if (tab === 'referred') {
        return `cases/referred?${pageQuery}${searchQuery}`;
      }
      return `cases/${tab}?${pageQuery}${searchQuery}`;
    }

    const value = severityFilter.split(' ').join('_').toUpperCase();
    if (tab === 'timeline') {
      return `cases?severity=${value}&${pageQuery}${searchQuery}`;
    }
    if (tab === 'referred') {
      return `cases/referred?severity=${value}&${pageQuery}${searchQuery}`;
    }
    if (tab === 'escalated' || tab === 'reported') {
      return `cases/filter/${tab}?severity=${value}&${pageQuery}${searchQuery}`;
    }
    return `cases/${tab}?severity=${value}&${pageQuery}${searchQuery}`;
  };

  const getCases = async (endpoint: string) => {
    setLoading(true);
    try {
      const response = await getFromUrl(endpoint);
      if (response && response.data && Array.isArray(response.data)) {
        setData(response.data);
        setTotalCount(response.pagination?.totalItems || response.data.length);
      } else if (
        response &&
        response.content &&
        Array.isArray(response.content)
      ) {
        setData(response.content);
        setTotalCount(response.totalElements || response.content.length);
      } else if (Array.isArray(response)) {
        setData(response);
        setTotalCount(response.length);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
      setData([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  const applyFilter = (
    tab = activeTab,
    page = currentPage,
    size = pageSize,
    severityFilter = filter,
    search = debouncedSearch
  ) => {
    getCases(buildEndpoint(tab, page, size, severityFilter, search)).catch(
      () => {
        return;
      }
    );
  };

  const handleTabs = (tab: string) => {
    setFilter('all');
    setCurrentPage(0);
    setActiveTab(tab);
    applyFilter(tab, 0, pageSize, 'all', debouncedSearch);
  };

  const handleSearchChange = (value: string) => {
    setSearchKeyword(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(0);
    }, 400);
  };

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value);
    setPageSize(nextSize);
    setCurrentPage(0);
  };

  const handlePageChange = (page: number | string) => {
    if (typeof page === 'number') {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    applyFilter(activeTab, currentPage, pageSize, filter, debouncedSearch);
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    if (activeTab !== 'timeline') {
      return;
    }
    const intervalId = setInterval(() => {
      applyFilter(activeTab, currentPage, pageSize, filter, debouncedSearch);
    }, 60000);
    return () => clearInterval(intervalId);
  }, [activeTab, currentPage, pageSize, filter, debouncedSearch]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);

  return (
    <DashboardLayout pageTitle='Cases'>
      <Box pr='60px' pb='54px'>
        <Flex justifyContent='space-between' alignItems='center' mb='30px'>
          <Tabs
            tabs={caseTabs}
            activeTab={activeTab}
            tabAction={handleTabs}
          />
          <Flex alignItems='center' gap='16px'>
            <Menu placement='bottom-end' gutter={8}>
              <MenuButton
                as={Flex}
                alignItems='center'
                justifyContent='space-between'
                gap='8px'
                h='40px'
                minW='118px'
                px='14px'
                bg='white'
                borderWidth='0.5px'
                borderColor='gray_200'
                borderRadius='15px'
                cursor='pointer'
                transition='all 0.15s ease'
                _hover={{ borderColor: 'muted', boxShadow: 'sm' }}
                _active={{ borderColor: 'deep_blue' }}
                _expanded={{ borderColor: 'deep_blue', boxShadow: 'sm' }}
                aria-label='Items per page'
                tabIndex={0}
              >
                <Body size='12px' color='gray' whiteSpace='nowrap'>
                  Per page
                </Body>
                <Flex alignItems='center' gap='4px'>
                  <Subtitle size='13px' color='deep_blue' fontWeight={600}>
                    {pageSize}
                  </Subtitle>
                  <ChevronDownIcon color='deep_blue' boxSize='16px' />
                </Flex>
              </MenuButton>
              <MenuList
                bg='white'
                border='none'
                borderRadius='12px'
                py='8px'
                minW='118px'
                boxShadow='0px 8px 16px rgba(8, 35, 48, 0.16)'
                zIndex={20}
              >
                {PAGE_SIZE_OPTIONS.map((size) => {
                  const isSelected = size === pageSize;
                  return (
                    <MenuItem
                      key={size}
                      onClick={() => handlePageSizeChange(String(size))}
                      bg={isSelected ? hexToRgbA(colors.deep_blue, '0.08') : 'transparent'}
                      color={isSelected ? 'deep_blue' : 'black'}
                      fontSize='13px'
                      fontWeight={isSelected ? 600 : 500}
                      fontFamily='Inter, sans-serif'
                      px='16px'
                      py='10px'
                      _hover={{
                        bg: hexToRgbA(colors.deep_blue, '0.08'),
                        color: 'deep_blue'
                      }}
                      _focus={{
                        bg: hexToRgbA(colors.deep_blue, '0.08')
                      }}
                    >
                      <Flex w='100%' justifyContent='space-between' alignItems='center'>
                        <span>{size}</span>
                        {isSelected && (
                          <Box
                            as='span'
                            className='iconlyBulk-Shield-Done'
                            fontSize='22px'
                            lineHeight='1'
                          >
                            <Box as='span' className='path1' display='none'></Box>
                            <Box as='span' className='path2' color='dark_green'></Box>
                          </Box>
                        )}
                      </Flex>
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
            <SearchInput
              value={searchKeyword}
              onChange={({ target: { value } }: any) =>
                handleSearchChange(value)
              }
              aria-label='Search cases'
            />
            <PopoverMenu
              placement='bottom-end'
              trigger={
                <Flex cursor='pointer' alignItems='center'>
                  <Icon
                    name='Filter'
                    color={colors.deep_blue}
                    size={18}
                    filled
                  />
                  <Subtitle color='deep_blue' ml='10px'>
                    Filter
                  </Subtitle>
                </Flex>
              }
              w='334px'
            >
              {(onClose: any) => (
                <Box px='8px' py='18px'>
                  <Body size='14px' color='black' textAlign='center' mb='22px'>
                    Show only Cases that are:
                  </Body>
                  {severityFilters.map((item, index) => {
                    const isSelected = item === filter;
                    return (
                      <Flex
                        h='48px'
                        justifyContent='center'
                        alignItems='center'
                        mb='8px'
                        bg={
                          isSelected
                            ? hexToRgbA(colors.black, '0.1')
                            : 'transparent'
                        }
                        pos='relative'
                        cursor='pointer'
                        onClick={() => setFilter(item)}
                        key={item + index}
                        tabIndex={0}
                        aria-label={`Filter by ${item}`}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            setFilter(item);
                          }
                        }}
                      >
                        <Subtitle textTransform='capitalize'>{item}</Subtitle>
                        {isSelected && (
                          <>
                            <Box
                              as='span'
                              className='iconlyBulk-Shield-Done'
                              pos='absolute'
                              right='24px'
                              fontSize='36px'
                            >
                              <Box
                                as='span'
                                className='path1'
                                display='none'
                              ></Box>
                              <Box
                                as='span'
                                className='path2'
                                color='dark_green'
                              ></Box>
                            </Box>
                          </>
                        )}
                      </Flex>
                    );
                  })}
                  <Box px='8px'>
                    <PrimaryButton
                      label='apply'
                      onClick={() => {
                        onClose();
                        setCurrentPage(0);
                        applyFilter(
                          activeTab,
                          0,
                          pageSize,
                          filter,
                          debouncedSearch
                        );
                      }}
                      height='55px'
                    />
                  </Box>
                </Box>
              )}
            </PopoverMenu>
          </Flex>
        </Flex>
        {!loading ? (
          <>
            {activeTab === 'timeline' && !data?.length && <EmptyCases />}
            {activeTab === 'escalated' && !data?.length && (
              <Text
                fontFamily='Inter, sans-serif'
                opacity={0.6}
                fontWeight={700}
                color='#756F86'
              >
                No escalated cases found
              </Text>
            )}
            {activeTab === 'reported' && !data?.length && (
              <Text
                fontFamily='Inter, sans-serif'
                opacity={0.6}
                fontWeight={700}
                color='#756F86'
              >
                No reported cases found
              </Text>
            )}
            {activeTab === 'assigned' && !data?.length && (
              <Text
                fontFamily='Inter, sans-serif'
                opacity={0.6}
                fontWeight={700}
                color='#756F86'
              >
                No assigned cases found
              </Text>
            )}
            {activeTab === 'closed' && !data?.length && (
              <Text
                fontFamily='Inter, sans-serif'
                opacity={0.6}
                fontWeight={700}
                color='#756F86'
              >
                No closed cases found
              </Text>
            )}
            {activeTab === 'referred' && !data?.length && (
              <Text
                fontFamily='Inter, sans-serif'
                opacity={0.6}
                fontWeight={700}
                color='#756F86'
              >
                No referred cases found
              </Text>
            )}

            <Grid gridTemplateColumns={'1fr 1fr 1fr'} gap='24px'>
              {data?.map((item: any, index: number) => (
                <CaseCard
                  data={item}
                  key={item._id}
                  index={index}
                  items={data}
                  listState={listState}
                />
              ))}
            </Grid>

            <Flex
              justifyContent='center'
              alignItems='center'
              mt='32px'
              gap='16px'
              flexWrap='wrap'
            >
              <PrimaryButton
                label='Prev'
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                height='40px'
              />
              <Text
                display='flex'
                alignItems='center'
                px='16px'
                fontSize='14px'
                fontWeight='500'
              >
                Page {currentPage + 1} of {totalPages}
              </Text>
              <PrimaryButton
                label='Next'
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1 || totalCount === 0}
                height='40px'
              />
              <Body size='12px' color='gray'>
                {totalCount} total
              </Body>
            </Flex>
          </>
        ) : null}
      </Box>
    </DashboardLayout>
  );
};

export default Cases;
